import './settings.scss';

import {
	DropdownMenu,
	type menuType,
} from 'components/DropdownMenu/DropdownMenu';
import {
	useGetPropertyRuleQuery,
	useGetRoundingRulesQuery,
	useUpdateRoundingRuleMutation,
} from 'features/rate-rounding/gql/_gen_/rate-rounding.gql';
import { useEffect, useState } from 'react';

import { Label } from '@/components/ui/label';
import { AgGridReact } from 'ag-grid-react';
import { usePropertyContext } from 'context/propertyContext';
import { useUser } from 'features/users/context/userContext';
import { useApolloError } from 'hooks/useApolloError';
import { useAppTracking } from 'hooks/useTracking';
import { labels } from '../../locales/en.label';
import { twoDecimalRateFormatter } from '../MyView/tableCustomCellRender';

export const Settings = () => {
	const [roundingRule, setRoundingRule] = useState(0);
	const { property, currencySymbol } = usePropertyContext();
	const propertyId = property?.propertyId;
	const { handleApolloError } = useApolloError();
	const { isReadOnly } = useUser();
	const { track } = useAppTracking();

	const { data: dataPropertyRule } = useGetPropertyRuleQuery({
		skip: !propertyId,
		variables: {
			propertyId: propertyId || '',
		},
	});

	const propertyRule = dataPropertyRule?.getPropertyByPropertyId?.rule_id;

	useEffect(() => {
		if (propertyRule && propertyRule !== roundingRule) {
			setRoundingRule(propertyRule);
		} else if (!propertyRule) {
			setRoundingRule(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [property, propertyRule]);

	const columnDefs = [
		{
			headerName: labels.settings.rounding_rules.column_headers.rule_id,
			field: 'rule_id',
			width: 100,
		},
		{
			headerName: labels.settings.rounding_rules.column_headers.price_bound,
			field: 'price_bound',
			width: 100,
			valueFormatter: (params: { value: number }) => {
				if (!params.value) return;
				const rate = twoDecimalRateFormatter(params?.value?.toString());
				return `${currencySymbol}${rate}`;
			},
		},
		{
			headerName: labels.settings.rounding_rules.column_headers.description,
			field: 'description',
			width: 250,
			tooltipField: 'description',
		},
		{
			headerName: labels.settings.rounding_rules.column_headers.format,
			field: 'format',
			width: 120,
		},
		{
			headerName: labels.settings.rounding_rules.column_headers.format_above,
			field: 'format_above_bound',
			width: 130,
		},
		{
			headerName: labels.settings.rounding_rules.column_headers.method,
			field: 'method',
			width: 120,
		},
		{
			headerName: labels.settings.rounding_rules.column_headers.use_bound,
			field: 'use_bound',
			width: 120,
		},
	];

	const { data: dataRoundingRules } = useGetRoundingRulesQuery();
	const filteredRules = dataRoundingRules?.getRoundingRules?.filter(
		(rule) => rule?.rule_id !== 0,
	);

	const [updateRoundingRule] = useUpdateRoundingRuleMutation();

	const getOptions = () => {
		if (!dataRoundingRules?.getRoundingRules) return [];

		const options = dataRoundingRules?.getRoundingRules?.map((rule) => {
			if (rule?.rule_id === 0) {
				return {
					label: labels.common.none,
					value: 0,
				};
			}
			return {
				label: rule?.rule_id?.toString(),
				value: rule?.rule_id,
			};
		}) as menuType[];

		return options.sort((a, b) => {
			if (typeof a.value === 'number' && typeof b.value === 'number')
				return a.value - b.value;
			return 0;
		}) as menuType[];
	};

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const ruleId = Number.parseInt(e.target.value);
		setRoundingRule(ruleId);

		if (propertyId) {
			updateRoundingRule({
				variables: {
					propertyId,
					ruleId: ruleId,
				},
				refetchQueries: ['GetPropertyRule'],
				onCompleted: () => {
					track({
						action: 'Update Rate Rounding Rule',
						propertyId,
						page: 'Settings',
						value: ruleId.toString(),
					});
				},
				onError: (error) => {
					handleApolloError(error);
				},
			});
		}
	};

	return (
		<div className='settings'>
			<h1 className='tw-text-2xl tw-font-semibold tw-mb-4 tw-pt-4  tw-pb-10'>
				Settings for {property?.propertyId}
			</h1>
			<h2 className='tw-text-lg tw-font-semibold tw-mb-2'>
				Rate Rounding Rules
			</h2>
			<hr className='tw-my-4 tw-border-gray' />
			<div className='rounding-rules tw-pb-10'>
				<form className='rounding-form'>
					<Label>Rate Rounding Rule</Label>
					<DropdownMenu
						options={getOptions()}
						preSelectValue={roundingRule}
						handleChange={handleChange}
						className='rule-select'
						disabled={isReadOnly}
					/>
				</form>
			</div>
			<div className='ag-theme-alpine rules-table'>
				<AgGridReact columnDefs={columnDefs as any} rowData={filteredRules} />
			</div>
		</div>
	);
};
